import type { Composer } from 'vue-i18n'
import type { ColorBucketKey, FilterColorGroupKey } from '~/constants/colorBuckets'
import { createColorCatalog } from '~~/lib/colorCatalog'
import { createColorCatalogRefresh } from '~~/lib/colorCatalogRefresh'
import { colorHex, isKnownLegacyColor } from '~/constants/colorAliases'
import { COLOR_BUCKET_KEYS, COLOR_BUCKET_SHADE_CODES, FILTER_EXTRA_COLOR_GROUPS } from '~/constants/colorBuckets'

export default defineNuxtPlugin({
  name: 'product-colors',
  // Locale headers and SSR cache-tag collection must be installed before fetching.
  dependsOn: ['graphql-config'],
  setup(nuxtApp) {
    // useAsyncData's app:created hook is awaited before page setup/SSR filters.
    // The computed index is per Nuxt app (per request on SSR), never module-global.
    const catalog = useColorCatalog()
    const lookup = computed(() => createColorCatalog(catalog.data.value ?? [], {
      hex: colorHex,
      known: isKnownLegacyColor,
      groups: { ...COLOR_BUCKET_SHADE_CODES, ...FILTER_EXTRA_COLOR_GROUPS },
    }))
    // Revalidate on navigation at most once per 30 seconds without blocking a
    // shopper who already has this market's palette. Hydration uses the payload.
    const i18n = nuxtApp.$i18n as Composer
    const refreshCatalog = createColorCatalogRefresh(() => catalog.refresh({ dedupe: 'defer' }))
    addRouteMiddleware('color-catalog', async () => {
      if (import.meta.client && !nuxtApp.isHydrating) {
        const key = `colorCatalog:${i18n.locale.value}`
        await refreshCatalog(key, Array.isArray(nuxtApp.payload.data[key]))
      }
    }, { global: true })
    return {
      provide: {
        productColors: {
          swatch: (token: string, facetCode?: string | null) => lookup.value.swatch(token, facetCode),
          entry: (token: string) => lookup.value.entry(token),
          entryById: (id: string) => lookup.value.entryById(id),
          known: (token: string) => lookup.value.known(token),
          groupForCode: (code: string) => lookup.value.groupForCode(code) as FilterColorGroupKey | null,
          bucketForCode: (code: string): ColorBucketKey | null => {
            const group = lookup.value.groupForCode(code)
            return (COLOR_BUCKET_KEYS as readonly string[]).includes(group ?? '') ? group as ColorBucketKey : null
          },
          groupCodes: (group: string) => lookup.value.groupCodes(group),
        },
      },
    }
  },
})
